'use client'

import { useEffect, useState, useCallback } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
    $getSelection,
    $isRangeSelection,
    BLUR_COMMAND,
    FOCUS_COMMAND,
    COMMAND_PRIORITY_LOW,
} from 'lexical'

/**
 * Draws a small vertical bar at the current Lexical caret position whenever
 * the editor doesn't have focus. The Lexical selection persists across
 * blurs, so we read it on demand and translate the anchor's DOM range into
 * an absolutely-positioned element inside the editor's `relative` wrapper.
 *
 * Position is recomputed on resize and on any scroll inside the document
 * so the marker stays glued to the underlying text as the surrounding
 * layout changes.
 */
export default function PersistentCaretPlugin() {
    const [editor] = useLexicalComposerContext()
    const [hasFocus, setHasFocus] = useState(true)
    const [position, setPosition] = useState(null)

    const compute = useCallback(() => {
        const root = editor.getRootElement()
        const wrapper = root?.parentElement
        if (!root || !wrapper) {
            setPosition(null)
            return
        }

        let result = null
        editor.getEditorState().read(() => {
            const selection = $getSelection()
            if (!$isRangeSelection(selection)) return

            const focus = selection.focus
            const anchorElement = editor.getElementByKey(focus.key)
            if (!anchorElement) return

            const range = document.createRange()
            let paragraphElement = null

            try {
                if (focus.type === 'element') {
                    // Element-level selection (empty paragraph, between
                    // children, etc). Set the range on the element itself.
                    const childCount = anchorElement.childNodes.length
                    const offset = Math.max(0, Math.min(focus.offset, childCount))
                    range.setStart(anchorElement, offset)
                    range.setEnd(anchorElement, offset)
                    paragraphElement = anchorElement.closest('p') ?? anchorElement
                } else {
                    // Text-level selection: Lexical's text node DOM is a
                    // <span> wrapping a single text child.
                    const textChild = anchorElement.firstChild
                    if (textChild && textChild.nodeType === Node.TEXT_NODE) {
                        const len = textChild.textContent?.length ?? 0
                        const offset = Math.max(0, Math.min(focus.offset, len))
                        range.setStart(textChild, offset)
                        range.setEnd(textChild, offset)
                    } else {
                        range.setStart(anchorElement, 0)
                        range.setEnd(anchorElement, 0)
                    }
                    paragraphElement = anchorElement.closest('p') ?? anchorElement
                }
            } catch {
                return
            }

            let rect = range.getBoundingClientRect()

            // Empty paragraphs render as <p><br></p>; the range rect can
            // be degenerate (zero height, and unreliable horizontal
            // position — Chrome can place it at the right edge of the
            // paragraph's text area). Reconstruct from the paragraph's own
            // rect, honoring its `text-align` so we line up with where
            // the browser would draw the native caret.
            if (rect.height === 0 && paragraphElement) {
                const pRect = paragraphElement.getBoundingClientRect()
                const align = getComputedStyle(paragraphElement).textAlign
                let left = pRect.left
                if (align === 'center') {
                    left = pRect.left + pRect.width / 2
                } else if (align === 'right' || align === 'end') {
                    left = pRect.right
                }
                rect = {
                    top: pRect.top,
                    left,
                    height: pRect.height || rect.height,
                }
            }

            const wrapperRect = wrapper.getBoundingClientRect()
            const fallbackHeight = parseFloat(getComputedStyle(root).lineHeight) || 18

            result = {
                top: rect.top - wrapperRect.top,
                left: rect.left - wrapperRect.left,
                height: rect.height || fallbackHeight,
            }
        })

        setPosition(result)
    }, [editor])

    useEffect(() => {
        const onFocus = () => { setHasFocus(true); return false }
        const onBlur = () => { setHasFocus(false); return false }

        const root = editor.getRootElement()
        setHasFocus(root != null && document.activeElement === root)

        const unregisterFocus = editor.registerCommand(FOCUS_COMMAND, onFocus, COMMAND_PRIORITY_LOW)
        const unregisterBlur = editor.registerCommand(BLUR_COMMAND, onBlur, COMMAND_PRIORITY_LOW)

        return () => {
            unregisterFocus()
            unregisterBlur()
        }
    }, [editor])

    useEffect(() => {
        if (hasFocus) {
            setPosition(null)
            return
        }

        compute()

        const onScroll = () => compute()
        const onResize = () => compute()

        window.addEventListener('resize', onResize)
        document.addEventListener('scroll', onScroll, true)

        const unregisterUpdate = editor.registerUpdateListener(() => compute())

        return () => {
            window.removeEventListener('resize', onResize)
            document.removeEventListener('scroll', onScroll, true)
            unregisterUpdate()
        }
    }, [hasFocus, editor, compute])

    if (hasFocus || !position) return null

    return (
        <div
            aria-hidden="true"
            className="pointer-events-none absolute w-[2px] bg-primary animate-pulse"
            style={{
                top: position.top,
                left: position.left,
                height: position.height,
            }}
        />
    )
}
